import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowRight, Search } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import SEO from "@/components/SEO";

const CATEGORIES = [
  "All",
  "The Great Wealth Transfer",
  "Minority Entrepreneurship",
  "Financial Literacy",
  "AI Governance & Technology",
  "Veteran Advocacy",
  "Leadership & Communication",
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

export default function Insights() {
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [sidebarEmail, setSidebarEmail] = useState("");
  const [sidebarStatus, setSidebarStatus] = useState("");

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const params = category === "All" ? "" : `?category=${encodeURIComponent(category)}`;
        const url = `/articles${params}${params ? "&" : "?"}published_only=true`;
        const response = await api.get(url);
        setArticles(response.data);
      } catch (err) {
        console.error("Failed to fetch articles:", err.message);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [category]);

  const handleSidebarSub = async (e) => {
    e.preventDefault();
    if (!sidebarEmail) return;
    try {
      await api.post("/subscribers", { email: sidebarEmail, list_type: "general" });
      setSidebarStatus("Subscribed!");
      setSidebarEmail("");
      setTimeout(() => setSidebarStatus(""), 4000);
    } catch { setSidebarStatus("Error."); }
  };

  return (
    <div>
      {/* HERO */}
      <SEO
        title="Insights"
        description="Original analysis by Dr. Ben Magee on the Great Wealth Transfer, minority entrepreneurship, financial literacy, AI governance, veteran advocacy, and leadership."
        keywords="Dr. Ben Magee insights, Great Wealth Transfer analysis, minority entrepreneurship, AI governance, veteran advocacy, financial literacy"
        page="insights"
        canonical="https://drbenmagee.com/insights"
      />
      <section className="bg-[#1B2A4A] py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="w-1 h-12 bg-[#C9A84C] mb-8" />
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight" data-testid="insights-hero-title">
            Insights
          </h1>
          <p className="mt-4 text-base md:text-lg text-white/60">
            Original analysis on wealth, entrepreneurship, technology, and service. No noise. No filler.
          </p>
        </div>
      </section>

      {/* FILTER + GRID */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          {/* CATEGORY FILTERS */}
          <div className="flex flex-wrap gap-2 mb-12" data-testid="insights-filters">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                  category === cat
                    ? "bg-[#C9A84C] text-[#1B2A4A]"
                    : "bg-[#E8EDF5] text-[#1B2A4A]/70 hover:bg-[#C9A84C]/20"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* ARTICLES GRID */}
            <div className="lg:col-span-3">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-[#E8EDF5] rounded-sm h-64 animate-pulse" />
                  ))}
                </div>
              ) : articles.length === 0 ? (
                <p className="text-[#1B2A4A]/60 text-center py-12">No articles found in this category.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {articles.map((a, i) => (
                    <motion.div
                      key={a.id}
                      custom={i}
                      initial="hidden"
                      animate="visible"
                      variants={fadeUp}
                      className="bg-[#E8EDF5] border border-[#1B2A4A]/10 rounded-sm overflow-hidden group hover:-translate-y-1 transition-transform duration-300"
                    >
                      <div className="h-1 bg-[#C9A84C]" />
                      <div className="p-6">
                        <Badge variant="secondary" className="text-xs mb-3 bg-[#1B2A4A]/10 text-[#1B2A4A] rounded-sm">
                          {a.category}
                        </Badge>
                        <h3 className="font-heading font-semibold text-lg text-[#1B2A4A] mb-2 line-clamp-2">{a.title}</h3>
                        <p className="text-xs text-[#1B2A4A]/40 mb-3">{new Date(a.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
                        <p className="text-sm text-[#1B2A4A]/60 line-clamp-2 mb-4">{a.excerpt}</p>
                        <Link
                          to={`/insights/${a.id}`}
                          data-testid={`insights-article-${i}`}
                          className="inline-flex items-center gap-2 text-sm font-semibold text-[#C9A84C] hover:text-[#b59744]"
                        >
                          Read More <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* SIDEBAR */}
            <aside className="space-y-8" data-testid="insights-sidebar">
              <div className="bg-[#E8EDF5] rounded-sm p-6 border border-[#1B2A4A]/10">
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src="https://customer-assets.emergentagent.com/job_benmagee-preview/artifacts/d5hw9875_9D68558F-415B-4B81-BC77-5CFE62AE5B4C.png"
                    alt="Dr. Ben Magee"
                    className="w-12 h-12 rounded-sm object-cover object-top border border-[#C9A84C]/30"
                  />
                  <h4 className="font-heading font-semibold text-base text-[#1B2A4A]">About the Author</h4>
                </div>
                <p className="text-sm text-[#1B2A4A]/60 leading-relaxed">
                  Dr. Ben Magee is a PhD, U.S. Army veteran, CISSP-certified technologist, MENSA International member, and serial entrepreneur building at the intersection of knowledge, technology, and service.
                </p>
              </div>

              <div className="bg-[#F5EDD6] rounded-sm p-6 border border-[#C9A84C]/20">
                <h4 className="font-heading font-semibold text-base text-[#1B2A4A] mb-3">Get New Insights</h4>
                <form onSubmit={handleSidebarSub} className="space-y-3">
                  <Input
                    type="email"
                    placeholder="Your email"
                    value={sidebarEmail}
                    onChange={(e) => setSidebarEmail(e.target.value)}
                    data-testid="sidebar-subscribe-email"
                    className="rounded-sm border-[#1B2A4A]/20"
                  />
                  <Button type="submit" data-testid="sidebar-subscribe-btn" className="w-full bg-[#C9A84C] text-[#1B2A4A] hover:bg-[#b59744] hover:text-white font-semibold rounded-sm">
                    Subscribe
                  </Button>
                  {sidebarStatus && <p className="text-xs text-[#1B2A4A] font-medium">{sidebarStatus}</p>}
                </form>
              </div>

              <div className="bg-[#1B2A4A] rounded-sm p-6">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#C9A84C] mb-2">Featured Book</p>
                <img 
                  src="https://customer-assets.emergentagent.com/job_063e6bfb-ec62-4703-a8ad-86932ecf54db/artifacts/ulccn5tw_IMG_5784.jpeg" 
                  alt="The Great Wealth Transfer by Dr. Ben Magee"
                  className="w-full h-auto rounded-sm mb-4"
                />
                <h4 className="font-heading font-semibold text-base text-white mb-2">The Great Wealth Transfer&trade;</h4>
                <p className="text-xs text-white/60 mb-4">Coming 2027</p>
                <Link to="/books">
                  <Button size="sm" className="bg-[#C9A84C] text-[#1B2A4A] hover:bg-[#b59744] hover:text-white rounded-sm text-xs">
                    Learn More
                  </Button>
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
